/**
 * Admin Login Page
 *
 * Dedicated admin authentication page with IC SSO only.
 * No email/password form, no social auth buttons, no consumer auth affordances.
 *
 * Security: This page is NOT linked from any public surface.
 * Admins navigate here directly at /admin/login.
 *
 * SCRUM-1721 — completes SCRUM-1115 (Admin Login with IC Redirect)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAccessToken } from '../../services/authService';
import { decodeJwtPayload } from '../../services/tokenRefreshInterceptor';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      const payload = decodeJwtPayload(token);
      const now = Math.floor(Date.now() / 1000);
      if (payload && typeof payload.exp === 'number' && payload.exp > now) {
        const returnTo = searchParams.get('returnTo');
        navigate(returnTo || '/admin/dashboard', { replace: true });
        return;
      }
    }
    setChecked(true);
  }, [navigate, searchParams]);

  if (!checked) {
    return null;
  }

  const handleSsoLogin = () => {
    // Redirect to the backend SAML initiation endpoint
    // The backend (GET /api/v1/auth/admin/login) redirects to IC_LOGIN_URL
    window.location.href = `${API_BASE_URL}/api/v1/auth/admin/login`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8fafb',
      padding: '1rem'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
        padding: '2.5rem',
        textAlign: 'center'
      }}>
        {/* Brand mark */}
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: '#009263',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: '#1a1a2e',
          marginBottom: '0.5rem'
        }}>
          Admin Sign In
        </h1>

        <p style={{
          fontSize: '0.875rem',
          color: '#6b7280',
          marginBottom: '2rem'
        }}>
          Sign in with your organization account
        </p>

        <button
          onClick={handleSsoLogin}
          data-testid="ic-sso-button"
          style={{
            width: '100%',
            padding: '0.875rem 1.5rem',
            fontSize: '0.9375rem',
            fontWeight: '600',
            color: '#ffffff',
            backgroundColor: '#009263',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.625rem',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#007a52'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#009263'; }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Sign in with Identity Center
        </button>

        <p style={{
          fontSize: '0.75rem',
          color: '#9ca3af',
          marginTop: '1.5rem'
        }}>
          OutdoorGuides Administration
        </p>
      </div>
    </div>
  );
}
